import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
    getEditorTableData,
    getAvailableUsers
} from "../../lib/supabase/schedule";
import JobEditorTable from "./components/JobEditorTable";
import { FiGrid, FiArrowLeft } from "react-icons/fi";

const EditorSchedule = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const { companySlug } = useParams();

    const [editorData, setEditorData] = useState([]);
    const [availableUsers, setAvailableUsers] = useState({ designers: [], estimators: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [showOnlyMine, setShowOnlyMine] = useState(false);
    const [selectedUser, setSelectedUser] = useState('');

    const [editorPagination, setEditorPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        hasMore: true
    });
    const [isLoadingMoreEditor, setIsLoadingMoreEditor] = useState(false);

    useEffect(() => {
        if (company?.id) {
            fetchData();
        }
    }, [company, showOnlyMine, selectedUser]);

    useEffect(() => {
        if (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') {
            fetchAvailableUsers();
        }
    }, [userProfile, company]);

    const fetchAvailableUsers = async () => {
        if (!company?.id) return;

        try {
            const users = await getAvailableUsers(company.id);
            setAvailableUsers(users);
        } catch (error) {
            console.error("Failed to fetch users:", error);
        }
    };

    const fetchData = async (loadMore = false) => {
        if (!company?.id) return;

        try {
            if (!loadMore) {
                setIsLoading(true);
            }

            const currentPage = loadMore ? editorPagination.page + 1 : 1;

            const filters = {
                userId: showOnlyMine ? userProfile?.id : selectedUser || null
            };

            if (loadMore) setIsLoadingMoreEditor(true);

            const result = await getEditorTableData(
                company.id,
                'editor',
                currentPage,
                editorPagination.limit,
                filters
            );

            if (loadMore) {
                setEditorData(prev => [...prev, ...result.data]);
            } else {
                setEditorData(result.data);
            }

            setEditorPagination(result.pagination);

            if (loadMore) setIsLoadingMoreEditor(false);
        } catch (error) {
            console.error("Error fetching editor data:", error);
        } finally {
            if (!loadMore) {
                setIsLoading(false);
            }
        }
    };

    const loadMoreEditorAssignments = () => {
        if (editorPagination.hasMore && !isLoadingMoreEditor) {
            fetchData(true);
        }
    };

    return (
        <>
            {/* Header */}
            <div className="mb-6">
                <button
                    onClick={() => navigate(`/${companySlug}/schedule`)}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
                >
                    <FiArrowLeft className="w-4 h-4" />
                    <span className="text-sm font-medium">Back to Schedules</span>
                </button>

                <div className="flex items-center gap-3">
                    <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                        <FiGrid className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Job Editor</h1>
                        <p className="text-gray-600 mt-1">
                            Comprehensive view and management of all construction jobs
                        </p>
                    </div>
                </div>
            </div>

            {/* User Filters */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="showOnlyMine"
                            checked={showOnlyMine}
                            onChange={(e) => {
                                setShowOnlyMine(e.target.checked);
                                if (e.target.checked) {
                                    setSelectedUser('');
                                }
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="showOnlyMine" className="ml-2 text-sm text-gray-700 font-medium">
                            Show only my assignments
                        </label>
                    </div>

                    {(userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                        <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-700 font-medium">Filter by user:</label>
                            <select
                                value={selectedUser}
                                onChange={(e) => {
                                    setSelectedUser(e.target.value);
                                    if (e.target.value) {
                                        setShowOnlyMine(false);
                                    }
                                }}
                                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-blue-500 focus:border-blue-500 min-w-[200px] bg-white"
                            >
                                <option value="">All users</option>

                                {availableUsers.designers && availableUsers.designers.length > 0 && (
                                    <optgroup label="Designers">
                                        {availableUsers.designers.map(user => (
                                            <option key={user.userId} value={user.userId}>
                                                {user.username}
                                            </option>
                                        ))}
                                    </optgroup>
                                )}

                                {availableUsers.estimators && availableUsers.estimators.length > 0 && (
                                    <optgroup label="Estimators">
                                        {availableUsers.estimators.map(user => (
                                            <option key={user.userId} value={user.userId}>
                                                {user.username}
                                            </option>
                                        ))}
                                    </optgroup>
                                )}
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* Job Editor Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <JobEditorTable
                    assignments={editorData}
                    userType={userProfile?.role}
                    company={companySlug}
                    navigate={navigate}
                    onLoadMore={loadMoreEditorAssignments}
                    hasMore={editorPagination.hasMore}
                    isLoadingMore={isLoadingMoreEditor}
                    isLoading={isLoading}
                    availableDesigners={availableUsers.designers || []}
                    scheduleType="editor"
                    showOnlyMine={showOnlyMine}
                    selectedUser={selectedUser}
                />
            </div>
        </>
    );
};

export default EditorSchedule;